/**
 * Idempotently install `window.mrRecommendations.refresh(selector, override?)`.
 *
 * Lives in its own file so the install is an explicit `installMr…()` call
 * site rather than a side effect of importing `useSearchProvider`. Keeps
 * the hook module pure for tests / SSR / type-only imports.
 *
 *   window.mrRecommendations.refresh(selector, recsConfigOverride?): number
 *
 * - `selector` matches one or more recs mounts (e.g. `[data-mini-cart-widget]`).
 * - `recsConfigOverride` (currently `{ seedProductId?: string }`) is forwarded
 *   to each mount's `__refreshRecs(override)` handler. When omitted, falls
 *   back to each match's `data-seed-product-id`. Canonical retarget idiom:
 *       el.dataset.seedProductId = newId;
 *       window.mrRecommendations.refresh('[data-mini-cart-widget]');
 * - Returns the count fired *synchronously*. Mounts not yet ready are queued
 *   on `mr:recs:ready` and aren't counted.
 *
 * Idempotent — multi-bundle pages collapse to a single install (first wins).
 */
export function installMrRecommendationsRefresh() {
    if (typeof window === 'undefined')
        return;
    const w = window;
    if (!w.mrRecommendations)
        w.mrRecommendations = {};
    if (typeof w.mrRecommendations.refresh === 'function')
        return;
    w.mrRecommendations.refresh = (selector, recsConfigOverride) => {
        if (!selector || typeof selector !== 'string') {
            console.warn('[mrRecommendations.refresh] A CSS selector argument is required.');
            return 0;
        }
        const matches = document.querySelectorAll(selector);
        if (matches.length === 0) {
            console.warn(`[mrRecommendations.refresh] No mounted carousel matched selector: ${selector}`);
            return 0;
        }
        let fired = 0;
        matches.forEach(el => {
            // Default the override from the mount's current dataset, so callers can
            // update `data-seed-product-id` and call `refresh(selector)` without
            // re-passing the seed. Explicit override always wins.
            const datasetSeed = el.dataset.seedProductId;
            const effectiveOverride = recsConfigOverride !== null && recsConfigOverride !== void 0 ? recsConfigOverride : (datasetSeed ? { seedProductId: datasetSeed } : undefined);
            const fn = el.__refreshRecs;
            if (typeof fn === 'function') {
                fn(effectiveOverride);
                fired++;
                return;
            }
            // Not mounted yet — queue via the ready event the host dispatches on mount.
            // `{ once: true }` auto-removes the listener after firing.
            el.addEventListener('mr:recs:ready', () => {
                const ready = el.__refreshRecs;
                if (typeof ready === 'function')
                    ready(effectiveOverride);
            }, { once: true });
        });
        return fired;
    };
}
